import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ExcelCopyIcon, PlusOutlined } from '../../components/icons'
import { Button } from '../../components/ui/Button'
import { Card } from '../../components/ui/Card'
import { Spinner } from '../../components/ui/Spinner'
import { useToast } from '../../components/ui/ToastProvider'
import { apiClient } from '../../lib/api/client'
import type { components } from '../../lib/api/schema.gen'
import { combinedItemCount, useCartCount } from '../../lib/cart/cart-count-context'
import { useRangeSelection } from '../../lib/list/useRangeSelection'
import { deliveryOptionForVariant } from '../rfq/variant-pricing'
import { AddLineForm } from './AddLineForm'
import { CartLineTable } from './CartLineTable'
import { CartServicesSection } from './CartServicesSection'
import { type CartPoGroup, CartSplitPoCard } from './CartSplitPoCard'
import { CopyFromExcelModal } from './CopyFromExcelModal'
import { CsvImportControl } from './CsvImportControl'
import { OrderSummaryRail } from './OrderSummaryRail'
import { QuantityMultiplier } from './QuantityMultiplier'
import { ReorderSourceNotice, distinctSourceOrders } from './ReorderSourceNotice'
import { ShipPlanCard } from './ShipPlanCard'
import { toAddCartLineRequest } from './cart-catalog-lookup'
import type { CartCsvRow } from './cart-csv'
import { CART_MAX_LINE_QUANTITY } from './cart-limits'
import { formatMoney } from './cart-money'
import { isKnownRegionTierId } from './custom-region-tiers'
import type { RegionTierId } from './custom-region-tiers'
import { importCatalogRows } from './import-catalog-rows'
import { formatBareShipDateLabel } from './ready-earlier'
import { deriveReadinessBucket } from './ship-as-ready-breakdown'
import { type SkuResolveService, defaultSkuResolveService } from './sku-resolver'

type CartView = components['schemas']['CartView']
type CartViewWithLineSpecifications = components['schemas']['CartViewWithLineSpecifications']
type Sourceable = components['schemas']['Sourceable']
type Quantity = components['schemas']['Quantity']
type ShipMode = components['schemas']['ShipMode']

/**
 * Client-side page size for the cart line-item table. The Cart BFF returns the whole
 * cart in one `GET /cart` (no server paging in the frozen contract), so paging here is
 * purely a display convenience for long carts; the order summary always totals the
 * FULL cart, not the visible page.
 */
const CART_PAGE_SIZE = 10

/**
 * How many per-row import errors the "Some rows couldn't be imported" banner
 * shows inline before collapsing the rest into a "+N more rows" summary. A
 * bulk import of a whole spreadsheet can produce hundreds of unresolved-row
 * messages; without this cap the banner grew to fill half the screen (and the
 * list also scrolls within a max-height, see cart.css). 8 is enough to show
 * the shape of the problem without dominating the page.
 */
const CART_IMPORT_ERROR_PREVIEW = 8

/**
 * Code-review Fix 3 — the single place that answers "which lines does the
 * ship-as-ready CUSTOM lead-time picker act on", shared by both
 * `handleSelectCustomLeadTimeTier` (which PUTs each one) and the render
 * body's `selectedCustomLeadTimeTierId` derivation (which reads the
 * persisted selection back) — previously each recomputed the same
 * `deriveReadinessBucket(...) === 'custom'` filter independently.
 */
function customBucketSourceableIdsOf(lines: CartView['lines']): string[] {
  return lines
    .filter((line) => deriveReadinessBucket(line.sourceable.kind) === 'custom')
    .map((line) => line.sourceable.id)
}

/**
 * The Cart screen (UID-59, WS-5, `/cart`) — rebuilt to the approved Cart
 * redesign mock and the Stage-1a `CartView` contract (ADR-0039):
 * ship-plan + delivery-option selection, a server-authoritative order
 * summary (nothing re-derived client-side), per-line presentation
 * (thumbnail/material/doc/ship-date/volume tiers), and bulk selection.
 * Mounted inside `<RequireAuth>` (App.tsx), so an unauthenticated visitor
 * never reaches this component — no auth branch needed here.
 */
export interface CartPageProps {
  /** Cross-source part-number resolver for the add-by-part-number + bulk-import
   * flows (empty-cart add box, CSV upload, CSV paste). Injectable for tests;
   * defaults to the real BFF-backed {@link defaultSkuResolveService}. */
  readonly skuResolver?: SkuResolveService
}

export function CartPage({ skuResolver = defaultSkuResolveService }: CartPageProps = {}) {
  const navigate = useNavigate()
  const location = useLocation()
  const { setLineCount } = useCartCount()
  /**
   * UID-403 (ADR-0125 D8) — the cart-side consumer of UID-401's one-shot
   * "generate quote" hand-off. `RfqReviewPage` navigates here with
   * `state: { openGenerateQuoteModal: true }` after adding the priced lines, and
   * this ticket owes the receiving half of that contract.
   *
   * CAPTURED ONCE, at mount, via a `useState` lazy initializer rather than a
   * `useMemo`/read of `location.state` — the auto-open decision must survive the
   * consume step below (which re-renders with the payload gone) and every later
   * cart mutation re-render.
   */
  const [autoOpenGenerateQuote] = useState<boolean>(() => {
    const handoff = location.state as { openGenerateQuoteModal?: boolean } | null
    return handoff?.openGenerateQuoteModal === true
  })
  /**
   * UID-433 gap #4 — the SAME one-shot handoff pattern, for
   * `WorkspaceUploadMenu`'s "Copy from Excel" item (which `navigate`s here
   * with `state: { openCopyFromExcel: true }`, matching the design comp's
   * own `applyXl()` → `this.go('cart')`). Captured once for the SAME reason
   * as `autoOpenGenerateQuote` above — must survive the consume step below.
   */
  const [autoOpenCopyFromExcel] = useState<boolean>(() => {
    const handoff = location.state as { openCopyFromExcel?: boolean } | null
    return handoff?.openCopyFromExcel === true
  })
  /**
   * CONSUME the payload — mandatory, not optional (ADR-0125 D8, citing ADR-0124
   * W3). `location.state` belongs to the HISTORY ENTRY, not this component: it
   * survives unmount and the browser restores it on reload, so an unconsumed
   * intent re-fires the modal on Back or refresh with no way to dismiss it.
   *
   * `replace: true` rewrites the current entry instead of pushing a new one (a
   * push would put a `/cart`-with-intent entry back on the Back stack, recreating
   * the bug one step away). Terminates by construction: after the replace
   * `location.state` is null and the effect returns at its guard. The already
   * captured `autoOpenGenerateQuote`/`autoOpenCopyFromExcel` are untouched, so
   * the modal stays open. Both handoff keys share this one effect/replace since
   * they're mutually exclusive (a nav can only carry one) and consuming either
   * has the identical shape.
   */
  useEffect(() => {
    const handoff = location.state as {
      openGenerateQuoteModal?: boolean
      openCopyFromExcel?: boolean
    } | null
    if (handoff?.openGenerateQuoteModal !== true && handoff?.openCopyFromExcel !== true) return
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null })
  }, [location, navigate])
  const { showToast } = useToast()
  const [cart, setCart] = useState<CartViewWithLineSpecifications>()
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string>()
  const [busyLineIds, setBusyLineIds] = useState<ReadonlySet<string>>(new Set())
  const [shipPlanBusy, setShipPlanBusy] = useState(false)
  const [savingShipTo, setSavingShipTo] = useState(false)
  const rangeSelection = useRangeSelection()
  const { selectedIds } = rangeSelection
  // Requested value-added services (UID-314/ADR-0105 projection; UID-423/
  // ADR-0127 persistence) — a real, server-PERSISTED field on the cart
  // (in-memory store; ADR-0127 §B), not a read-time-only seed. These are
  // populated ONLY by upstream processes (BOM/RFQ/other) via the API
  // (UID-424) — the cart screen shows and removes them but never adds one.
  // Remove round-trips through `DELETE /cart/services/{id}` below
  // (`handleRemoveService`), superseding the old UID-310-stub-parity
  // local-dismissal behavior — a removed service now stays removed on
  // reload within the session, not just this render.
  const requestedServices = cart?.requestedServices ?? []
  // Busy-tracking for in-flight `DELETE /cart/services/{id}` calls, kept
  // SEPARATE from `busyLineIds` (below) — service ids and line/sourceable ids
  // are different id spaces, and mixing them into one Set would risk a false
  // "busy" collision if they ever coincide.
  const [busyServiceIds, setBusyServiceIds] = useState<ReadonlySet<string>>(new Set())
  // Split-PO (UID-315, ADR-0106): the buyer's choice is SERVER-persisted on the
  // cart (`PUT /cart/split-po`) and carried to checkout (which reads it from its
  // GET /cart and is choice-driven). The per-PO breakdown is server-derived onto
  // `CartView.poBreakdown` (labels-only projection; ADR-0029). The card renders
  // FORMATTED amounts, so map the server's decimal-string `amountLabel` through
  // `formatMoney` here (same server-decimal → FE-format pattern as the summary).
  // True while ANY cart mutation is in flight, ACROSS every kind (a line
  // quantity/config edit, a service removal, a ship-plan/delivery-option
  // change, or a ship-to save) — not just the kind each individual busy flag
  // already tracks. Gates every summary-rail action (Checkout, Generate
  // quote, Separate-POs toggle, shipment grouping, quantity multiplier) so
  // none of them can fire against a cart snapshot that's still
  // mid-recalculation server-side, regardless of WHICH mutation is pending.
  const cartMutationPending =
    busyLineIds.size > 0 || busyServiceIds.size > 0 || shipPlanBusy || savingShipTo
  const splitPoEnabled = cart?.splitPoEnabled ?? false
  const poGroups: readonly CartPoGroup[] = (cart?.poBreakdown ?? []).map((group) => ({
    id: group.key,
    poLabel: group.poLabel,
    name: group.name,
    amountLabel: formatMoney(group.amountLabel),
  }))
  const [importErrors, setImportErrors] = useState<readonly string[]>([])
  const [showAddRow, setShowAddRow] = useState(false)
  const [showCopyFromExcel, setShowCopyFromExcel] = useState(autoOpenCopyFromExcel)
  const [page, setPage] = useState(1)
  // UID-271 — the custom-bucket lead-time tier round trip
  // (`PUT /cart/lines/{sourceableId}/lead-time-tier`) is in flight. The
  // SELECTED tier itself is no longer page-local state (superseding UID-96
  // C2's "static FE table, never round-tripped" posture, ADR-0085) — it's
  // derived below from the server's `cart.customLeadTimeSelections` so a
  // reload always shows the persisted choice, never a client default that
  // silently disagrees with the cart.
  const [customLeadTimeTierBusy, setCustomLeadTimeTierBusy] = useState(false)
  // WCAG F5: removing a line destroys the element that had focus, dropping
  // focus to <body>. After a successful remove we move focus to the cart
  // heading — a target that is always present (list populated OR empty), so
  // keyboard/screen-reader users keep a stable, meaningful place in the page.
  const headingRef = useRef<HTMLHeadingElement>(null)
  // The currently-visible (paged) line ids, for `toggleSelectAll` — kept as
  // a plain ref (assigned during render, below, once `pagedLines` is known)
  // rather than a `useCallback` dependency dance or extra state; selecting
  // "all" doesn't itself need to trigger a re-render beyond the selection
  // it causes.
  const pagedLinesRef = useRef<string[]>([])
  // "Multiply all cart quantities" state. The applied factor is persistent
  // (drives the multiplier badge and the Reset affordance), and the per-line
  // BASE (pre-multiply) quantities are captured before the first multiply so
  // the operation stays reversible and re-applying a new factor is computed
  // from base — never compounding the already-multiplied value. Base is a ref,
  // not state: it feeds the PATCH loop but nothing renders from it directly, so
  // mutating it must not trigger a re-render.
  const [appliedMultiplier, setAppliedMultiplier] = useState(1)
  const baseQuantitiesRef = useRef<Map<string, number>>(new Map())

  const loadCart = useCallback(async () => {
    setLoading(true)
    setLoadError(undefined)
    try {
      const { data, error } = await apiClient.GET('/cart')
      if (data) {
        setCart(data)
        return
      }
      if (error?.status === 401) {
        return // Handled globally — authMiddleware flips auth.status, RequireAuth redirects.
      }
      setLoadError(error?.detail ?? "Couldn't load your cart. Please try again.")
    } catch {
      setLoadError('Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadCart()
  }, [loadCart])

  // Keep the global top-bar cart badge in sync with the authoritative cart.
  // Only pushes once the cart has actually loaded (`cart` defined) — a failed
  // load leaves the last-known count untouched rather than zeroing the badge.
  // UID-423 (ADR-0127 §H) — the badge is a COMBINED count (part/BOM lines +
  // requested services), pushed the same way everywhere else in the app
  // pushes it, so it never contradicts the "N items" subtitle just below.
  useEffect(() => {
    if (cart) setLineCount(combinedItemCount(cart))
  }, [cart, setLineCount])

  // UID-617 — the design always shows the delivery-option selector's FIRST row
  // (`CartQuoteOptionSelector`'s own `RFQ_QUOTE_VARIANTS[0]` default) as checked, but
  // a cart that has never had a REAL selection persists `selectedDeliveryOptionId:
  // null` until the buyer explicitly picks one (`resolveCheckoutReadiness` requires a
  // real id) — so the radio LOOKED selected while "Continue to checkout" stayed
  // disabled, with no visible reason why. Rather than either (a) making the radio
  // show nothing selected (a worse design regression) or (b) changing the backend's
  // null default itself (touches pricing everywhere a delivery option is priced, and
  // collides with the ship_as_ready-detour memory restore, UID-617 above), this
  // auto-persists that SAME first-row id the instant it's shown checked, so the
  // backend catches up to what the screen already displays. Guarded by cart id (not a
  // bare fired-once boolean) so a LATER cart — e.g. after an order empties this one
  // and its shipPlan resets to null too (UID-617's own empty-cart reset) — still gets
  // its own default applied; never fires on an empty cart (nothing to ship yet) or
  // while another ship-plan mutation is already in flight.
  const deliveryOptionDefaultAppliedForCartIdRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (!cart || shipPlanBusy) return
    if (cart.lines.length === 0) return
    if (cart.shipPlan?.mode !== 'ship_together' || cart.shipPlan.selectedDeliveryOptionId) return
    if (deliveryOptionDefaultAppliedForCartIdRef.current === cart.id) return
    const defaultDeliveryOptionId = deliveryOptionForVariant('us')
    if (defaultDeliveryOptionId === undefined) return
    deliveryOptionDefaultAppliedForCartIdRef.current = cart.id
    void handleSelectDeliveryOption(defaultDeliveryOptionId)
  }, [cart, shipPlanBusy])

  // A line removed out from under a pending selection must not leave a
  // stale id selected forever — prune on every cart update. `selectedIds`
  // is a real dependency here (unlike the old `setSelectedIds(current => …)`
  // functional-updater version, which didn't need it): the branch below
  // only calls `setSelectedIds` when it actually shrinks the set, so the
  // re-run that follows recomputes against the now-already-pruned set and
  // no-ops — it settles in one extra pass, never loops.
  // biome-ignore lint/correctness/useExhaustiveDependencies: rangeSelection.setSelectedIds closes over the same stable setState every render, so omitting it doesn't risk a stale write.
  useEffect(() => {
    if (!cart) return
    const validIds = new Set(cart.lines.map((line) => line.sourceable.id))
    const next = new Set([...selectedIds].filter((id) => validIds.has(id)))
    if (next.size !== selectedIds.size) {
      rangeSelection.setSelectedIds(next)
    }
  }, [cart, selectedIds])

  function withBusy<T>(sourceableId: string, run: () => Promise<T>): Promise<T> {
    setBusyLineIds((current) => new Set(current).add(sourceableId))
    return run().finally(() => {
      setBusyLineIds((current) => {
        const next = new Set(current)
        next.delete(sourceableId)
        return next
      })
    })
  }

  /** Same busy-tracking shape as `withBusy`, above, but over `busyServiceIds`
   * (a distinct id space — see that state's own doc comment). */
  function withServiceBusy<T>(serviceId: string, run: () => Promise<T>): Promise<T> {
    setBusyServiceIds((current) => new Set(current).add(serviceId))
    return run().finally(() => {
      setBusyServiceIds((current) => {
        const next = new Set(current)
        next.delete(serviceId)
        return next
      })
    })
  }

  async function patchLineQuantity(sourceableId: string, uom: Quantity['uom'], nextValue: number) {
    const { data, error } = await apiClient.PATCH('/cart/lines/{sourceableId}', {
      params: { path: { sourceableId } },
      body: { quantity: { value: nextValue, uom } },
    })
    if (data) {
      setCart(data)
      return true
    }
    if (error?.status !== 401) {
      setLoadError(error?.detail ?? 'Could not update that quantity. Please try again.')
    }
    return false
  }

  async function handleQuantityChange(sourceableId: string, nextValue: number) {
    const line = cart?.lines.find((candidate) => candidate.sourceable.id === sourceableId)
    if (!line) return
    await withBusy(sourceableId, () =>
      patchLineQuantity(sourceableId, line.quantity.uom, nextValue),
    )
  }

  /**
   * UID-434/WS-E — the "Edit configuration" shell's own save seam
   * (`CartLineRow`'s `saveSeam`) calls `cart-line-configurator.ts`'s
   * `defaultCartLineConfiguratorService` directly (the SAME `PATCH
   * /cart/lines/{sourceableId}` this page's own quantity stepper hits), and
   * hands the resulting FULL next cart back here so the page re-renders from
   * it exactly like every other cart mutation on this screen — subtotal,
   * `lineSpecifications`, readiness chips, all server-derived, never
   * recomputed client-side.
   */
  function handleConfigurationSaved(next: CartViewWithLineSpecifications) {
    setCart(next)
  }

  async function deleteLine(sourceableId: string) {
    const { data, error } = await apiClient.DELETE('/cart/lines/{sourceableId}', {
      params: { path: { sourceableId } },
    })
    if (data) {
      setCart(data)
      return true
    }
    if (error?.status !== 401) {
      setLoadError(error?.detail ?? 'Could not remove that line. Please try again.')
    }
    return false
  }

  async function handleRemove(sourceableId: string) {
    await withBusy(sourceableId, () => deleteLine(sourceableId))
    // WCAG F5: the removed row's controls are gone — restore focus to the
    // always-present cart heading so it never falls to <body>.
    headingRef.current?.focus()
  }

  /** Removes a requested service via `DELETE /cart/services/{id}`, mirroring
   * `deleteLine`'s shape. 404 ("already removed") and any other failure both
   * surface as a `showToast` — buyer-actionable, not a systemic load
   * failure, matching the toast pattern already used for
   * `handleDuplicateLine`'s max-quantity notice and `handleClearCart`. */
  async function removeService(serviceId: string): Promise<boolean> {
    const { data, error } = await apiClient.DELETE('/cart/services/{serviceId}', {
      params: { path: { serviceId } },
    })
    if (data) {
      setCart(data)
      return true
    }
    if (error?.status === 401) {
      return false // Handled globally.
    }
    if (error?.status === 404) {
      showToast(error.detail ?? 'That service was already removed.', 'error')
    } else {
      showToast(error?.detail ?? 'Could not remove that service. Please try again.', 'error')
    }
    return false
  }

  async function handleRemoveService(serviceId: string) {
    await withServiceBusy(serviceId, () => removeService(serviceId))
  }

  /** Removes every selected line in ONE atomic `POST /cart/lines/remove-batch`
   * call, rather than looping `deleteLine` per id — the prior loop was N
   * serialized CAS transactions (deliberately sequential, never `Promise.all`,
   * to avoid racing each DELETE's own full-cart response against the others),
   * where this is one request with one response. Marks every selected id busy
   * for the single request's duration, mirroring `withBusy`'s shape. */
  async function handleRemoveSelected() {
    const ids = [...selectedIds]
    if (ids.length === 0) return
    setBusyLineIds((current) => new Set([...current, ...ids]))
    try {
      const { data, error } = await apiClient.POST('/cart/lines/remove-batch', {
        body: { sourceableIds: ids },
      })
      if (data) {
        setCart(data)
      } else if (error?.status !== 401) {
        setLoadError(error?.detail ?? 'Could not remove the selected lines. Please try again.')
      }
    } finally {
      setBusyLineIds((current) => {
        const next = new Set(current)
        for (const id of ids) next.delete(id)
        return next
      })
    }
    rangeSelection.clear()
    headingRef.current?.focus()
  }

  /** Design-fidelity fix: the prototype clears immediately (no confirmation
   * dialog — `flash('Cart cleared')`) rather than gating on a confirm
   * modal; matched here with the same `showToast` pattern already used
   * elsewhere on this page. Still preserves the WCAG F5 focus-restore to
   * the cart heading after every line is gone.
   *
   * UID-428: a SINGLE atomic `POST /cart/clear` empties the whole cart server-side
   * (every line AND every requested service, per the option-A package-level
   * invariant) in one request, superseding the prior per-line
   * `DELETE /cart/lines/{id}` loop that made parts vanish one at a time. `setCart`
   * from the returned empty cart re-renders in one shot; the combined badge falls to
   * 0 via the existing effect. */
  /** UID-431 (ADR-0139 D5 / UX spec §3) — `GenerateQuoteModal`'s `onQuoteGenerated`:
   * fired once, when that modal closes after a successful `POST
   * /cart/generate-quote`. That endpoint clears the cart server-side but deliberately
   * does NOT inline the resulting empty cart in its response, so this re-GETs `/cart`
   * (rather than a direct `setCart`) to drive the `hasLines` → `CartEmptyState`
   * transition, then shows the toast — same `showToast` pattern `handleClearCart`
   * already uses for the manual-clear action, with more specific copy so the two
   * triggers are distinguishable in a session where a buyer does both. */
  async function handleQuoteGenerated() {
    await loadCart()
    showToast('Cart cleared — quote generated')
  }

  async function handleClearCart() {
    const { data, error } = await apiClient.POST('/cart/clear', {})
    if (data) {
      setCart(data)
      rangeSelection.clear()
      setImportErrors([])
      showToast('Cart cleared')
      headingRef.current?.focus()
      return
    }
    if (error?.status !== 401) {
      setLoadError(error?.detail ?? 'Could not clear the cart. Please try again.')
    }
  }

  async function handleAddLine(sourceable: Sourceable, quantity: Quantity) {
    const { data, error } = await apiClient.POST('/cart/lines', {
      body: toAddCartLineRequest(sourceable, quantity),
    })
    if (data) {
      setCart(data)
      return
    }
    if (error?.status === 401) {
      return // Handled globally.
    }
    // The cart is one-line-per-sourceable, so adding a SKU that's already in
    // the cart returns 409 ("…already has a line… update its quantity
    // instead"). Rather than surface that raw error (which named the internal
    // sourceable UUID), MERGE: bump the existing line's quantity by the amount
    // requested — the behavior a shopper expects when re-adding an item, and
    // shared by Add parts / Copy from Excel / Upload CSV (all route here).
    if (error?.status === 409) {
      // Merge onto the server's CURRENT quantity, not whatever local state
      // happens to hold: if the line isn't in local `cart.lines` (stale or
      // null state — e.g. another tab added it), refetch first so we (a) never
      // fall through to the raw 409 detail, which names the internal sourceable
      // UUID, and (b) narrow the lost-update window before the absolute PATCH.
      // The window can't fully close while the contract's PATCH sets an
      // absolute value rather than incrementing server-side.
      let existing = cart?.lines.find((line) => line.sourceable.id === sourceable.id)
      if (!existing) {
        const refetched = await apiClient.GET('/cart')
        if (refetched.data) {
          setCart(refetched.data)
          existing = refetched.data.lines.find((line) => line.sourceable.id === sourceable.id)
        }
      }
      if (existing) {
        const nextValue = Math.min(CART_MAX_LINE_QUANTITY, existing.quantity.value + quantity.value)
        const patched = await apiClient.PATCH('/cart/lines/{sourceableId}', {
          params: { path: { sourceableId: sourceable.id } },
          body: { quantity: { value: nextValue, uom: existing.quantity.uom } },
        })
        if (patched.data) {
          setCart(patched.data)
          return
        }
        if (patched.error?.status === 401) return
        throw new Error(patched.error?.detail ?? 'Failed to update line quantity')
      }
      // 409, but we still can't locate the line to merge onto — surface a
      // shopper-friendly message, never the raw internal detail.
      throw new Error('That item is already in your cart — refresh to adjust its quantity.')
    }
    throw new Error(error?.detail ?? 'Failed to add line')
  }

  async function handleParsedBulkRows({
    rows,
    errors,
  }: { rows: readonly CartCsvRow[]; errors: readonly string[] }) {
    // Shared with `HomePage`'s "Copy from Excel" import
    // (`import-catalog-rows.ts`) — only the per-row resolve+add+error loop is
    // shared; `handleAddLine` (this page's own merge-on-409 add, which also
    // updates `cart` state) stays the add path here, same as before. Resolution
    // now spans BOTH catalogs via the shared cross-source resolver (UID-59
    // follow-up), replacing the fixture-only lookup.
    const { errors: combinedErrors } = await importCatalogRows(
      { rows, errors },
      {
        resolve: (partNumber) => skuResolver.resolve(partNumber),
        addLine: (sourceable, row) =>
          handleAddLine(sourceable, { value: row.quantity, uom: sourceable.uom.saleUom }),
      },
    )
    setImportErrors(combinedErrors)
  }

  async function handleSelectMode(mode: ShipMode) {
    setShipPlanBusy(true)
    try {
      const { data, error } = await apiClient.PUT('/cart/ship-plan', { body: { mode } })
      if (data) {
        setCart(data)
        return
      }
      if (error?.status !== 401) {
        setLoadError(error?.detail ?? 'Could not update the ship plan. Please try again.')
      }
    } finally {
      setShipPlanBusy(false)
    }
  }

  // UID-315 (ADR-0106): persist the split-PO choice on the cart and re-render from the
  // returned CartView (which carries the re-derived poBreakdown). Mirrors handleSelectMode.
  async function handleToggleSplitPo(enabled: boolean) {
    const { data, error } = await apiClient.PUT('/cart/split-po', { body: { enabled } })
    if (data) {
      setCart(data)
      return
    }
    if (error?.status !== 401) {
      setLoadError(error?.detail ?? 'Could not update the split-PO choice. Please try again.')
    }
  }

  async function handleSelectDeliveryOption(deliveryOptionId: string) {
    setShipPlanBusy(true)
    try {
      const { data, error } = await apiClient.PUT('/cart/delivery-option', {
        body: { deliveryOptionId },
      })
      if (data) {
        setCart(data)
        return
      }
      if (error?.status !== 401) {
        setLoadError(error?.detail ?? 'Could not update the delivery option. Please try again.')
      }
    } finally {
      setShipPlanBusy(false)
    }
  }

  /**
   * UID-271 (Part A) — round-trips the "Ship as ready" custom bucket's
   * lead-time tier choice through `PUT /cart/lines/{sourceableId}/lead-time-tier`,
   * the SAME mechanism `handleSelectDeliveryOption` above uses for the
   * ship-together delivery option: one apiClient call per affected line,
   * taking the returned re-derived `CartView` as the new cart state — never
   * recomputing the summary/subtotal locally. Every custom-bucket line
   * (see the "custom"-only filter's own comment, below, for why
   * "configurable" is excluded) gets the SAME tier (the UI shows one
   * control for the whole bucket), so this loops sequentially — same
   * reasoning as `handleRemoveSelected`: each PUT returns the FULL next
   * cart, and firing them concurrently would race those responses against
   * each other.
   *
   * Code-review H1 — this is best-effort per line, NOT all-or-nothing: on a
   * cart with 2+ custom-bucket lines, a failure on line N>1 (a transient
   * 500/network error — a pure 422 unknown-tier-id fails identically on
   * every line, so it always surfaces on line 1) means lines `1..N-1`
   * already succeeded and ARE persisted server-side, while `N..end` are
   * not — a genuinely MIXED persisted state, which does not self-heal on
   * reload. On ANY failure (422 or otherwise) this now does a single
   * `GET /cart` refetch and sets cart state from THAT before surfacing the
   * error, so the displayed tier + summary always reflect what's actually
   * persisted — never the stale partial state from the last successful
   * `setCart` in the loop. Second code-review pass (H1 follow-up): if that
   * RECOVERY refetch itself fails, we still surface an error rather than
   * silently leaving the last (possibly stale) `cart` state on screen —
   * see the `refetched.data` branch below.
   *
   * Code-review Fix 4 — the error split below is intentional, not
   * arbitrary: a 422 (unknown tier id) is USER-actionable — the buyer just
   * picked a tier the server rejected, so it's a transient `showToast` they
   * can dismiss and retry — while everything else (5xx, network failure,
   * and a failed recovery refetch) is SYSTEMIC, so it goes to the
   * persistent `loadError` banner (the same mechanism every other cart
   * mutation on this page uses for that class of failure).
   */
  async function handleSelectCustomLeadTimeTier(tierId: RegionTierId) {
    // Code-review M2: "custom" bucket only (not "configurable") is
    // intentional — see `customBucketSourceableIdsOf`'s own doc, above,
    // where the picker's displayed selection is derived the same way.
    const customBucketSourceableIds = customBucketSourceableIdsOf(cart?.lines ?? [])
    if (customBucketSourceableIds.length === 0) return
    setCustomLeadTimeTierBusy(true)
    try {
      for (const sourceableId of customBucketSourceableIds) {
        const { data, error } = await apiClient.PUT('/cart/lines/{sourceableId}/lead-time-tier', {
          params: { path: { sourceableId } },
          body: { leadTimeTierId: tierId },
        })
        if (data) {
          setCart(data)
          continue
        }
        if (error?.status === 401) {
          break // Handled globally — authMiddleware flips auth.status, RequireAuth redirects.
        }
        // Re-establish a single authoritative view before surfacing
        // anything (H1): lines already PUT above ARE persisted even though
        // this one failed, so the cart's current in-memory state (from the
        // last successful iteration, or the pre-change cart on a line-1
        // failure) may already disagree with the server. A plain GET
        // resolves that ambiguity in one shot.
        const refetched = await apiClient.GET('/cart')
        if (refetched.data) {
          setCart(refetched.data)
        } else if (refetched.error?.status !== 401) {
          // H1 follow-up: the RECOVERY refetch itself failed — the user
          // must never be left staring at a silently-stale cart with no
          // indication anything went wrong. Surface it the same way every
          // other systemic failure on this page does.
          setLoadError(
            refetched.error?.detail ?? 'Could not refresh your cart. Please reload the page.',
          )
        }
        if (error?.status === 422) {
          showToast(
            error.detail ?? 'That lead-time option isn’t available right now. Please try another.',
            'error',
          )
        } else {
          setLoadError(error?.detail ?? 'Could not update the lead-time tier. Please try again.')
        }
        break
      }
    } finally {
      setCustomLeadTimeTierBusy(false)
    }
  }

  async function handleShipToChange(shipToAddressId: string) {
    setSavingShipTo(true)
    try {
      const { data, error } = await apiClient.PUT('/cart/ship-to', { body: { shipToAddressId } })
      if (data) {
        setCart(data)
        return
      }
      if (error?.status !== 401) {
        setLoadError(error?.detail ?? 'Could not update the ship-to address. Please try again.')
      }
    } finally {
      setSavingShipTo(false)
    }
  }

  /** Create a new ship-to address and surface it on the cart. Returns the new
   * address id so the rail can show it selected.
   *
   * Writes to the SAME account address book the checkout step uses
   * (`POST /account/addresses`, ADR-0044 §2) — so the address is really
   * persisted and flows through to checkout and a placed order. We then
   * refetch the cart: its ship-to LIST is re-derived from the account book on
   * `GET /cart`, so the new address appears in the dropdown. We deliberately
   * do NOT `PUT /cart/ship-to` — the cart's SELECTION endpoint only accepts
   * its original seeded ids and 422s ("Unknown ship-to address") for a
   * freshly-created one, so the rail selects it client-side for display while
   * the real, tax-affecting selection happens at checkout. Rejects on a failed
   * create so the modal shows its own error. */
  async function handleCreateShipToAddress(input: {
    label: string
    contactName: string
    companyName: string
    line1: string
    line2: string
    city: string
    region: string
    postalCode: string
    isDefault: boolean
  }): Promise<string> {
    const created = await apiClient.POST('/account/addresses', {
      body: {
        label: input.label.trim(),
        line1: input.line1.trim(),
        line2: input.line2.trim() || null,
        city: input.city.trim(),
        region: input.region.trim(),
        postalCode: input.postalCode.trim(),
        countryCode: 'US',
        contactName: input.contactName.trim() || null,
        companyName: input.companyName.trim() || null,
        ...(input.isDefault ? { isDefault: true } : {}),
      },
    })
    if (!created.data) {
      throw new Error(created.error?.detail ?? 'createAddress failed')
    }
    const newId = created.data.addressId
    // Refetch so the cart's ship-to list (re-derived from the account book)
    // includes the new address — the rail then selects it by id for display.
    const refreshed = await apiClient.GET('/cart')
    if (refreshed.data) setCart(refreshed.data)
    return newId
  }

  async function handleApplyMultiplier(multiplier: number) {
    const lines = cart?.lines ?? []
    const base = baseQuantitiesRef.current
    // Capture each line's base (pre-multiply) quantity the first time we see
    // it, before any PATCH changes it. Storing the raw quantity — never the
    // clamped/multiplied value — keeps the base uncorrupted so Reset can
    // always restore the true original.
    for (const line of lines) {
      if (!base.has(line.sourceable.id)) {
        base.set(line.sourceable.id, line.quantity.value)
      }
    }
    for (const line of lines) {
      const baseValue = base.get(line.sourceable.id) ?? line.quantity.value
      // Always base × factor, so applying 4× after 3× replaces rather than
      // compounds. The clamp guards the PATCHed target only — base is untouched.
      const nextValue = Math.min(CART_MAX_LINE_QUANTITY, baseValue * multiplier)
      if (nextValue === line.quantity.value) continue
      await withBusy(line.sourceable.id, () =>
        patchLineQuantity(line.sourceable.id, line.quantity.uom, nextValue),
      )
    }
    setAppliedMultiplier(multiplier)
  }

  async function handleResetMultiplier() {
    const lines = cart?.lines ?? []
    const base = baseQuantitiesRef.current
    for (const line of lines) {
      const baseValue = base.get(line.sourceable.id)
      if (baseValue === undefined || baseValue === line.quantity.value) continue
      await withBusy(line.sourceable.id, () =>
        patchLineQuantity(line.sourceable.id, line.quantity.uom, baseValue),
      )
    }
    baseQuantitiesRef.current = new Map()
    setAppliedMultiplier(1)
  }

  function handleProceedToCheckout() {
    navigate('/checkout')
  }

  /** UID-610 — shift-click range selection over the cart's currently PAGED
   * line order (`pagedLinesRef`), same Gmail/Finder semantics as UID-470's
   * Parts Library. `shiftKey` reaches here from `CartLineRow`'s checkbox
   * `onClick` (via `CartLineTable`) rather than `onChange`, since a
   * `ChangeEvent` carries no modifier-key state. */
  function toggleSelected(sourceableId: string, shiftKey: boolean) {
    rangeSelection.toggle(sourceableId, pagedLinesRef.current, shiftKey)
  }

  function toggleSelectAll(selected: boolean) {
    rangeSelection.toggleAll(pagedLinesRef.current, selected)
  }

  if (loading) {
    return (
      <section aria-labelledby="cart-heading">
        <h1 id="cart-heading" className="display-heading">
          Cart
        </h1>
        <Spinner label="Loading your cart" />
      </section>
    )
  }

  if (loadError && !cart) {
    return (
      <section aria-labelledby="cart-heading">
        <h1 id="cart-heading" className="display-heading">
          Cart
        </h1>
        <div role="alert" className="empty-state-panel">
          <p className="empty-state-title">Couldn&rsquo;t load your cart</p>
          <p className="empty-state-detail">{loadError}</p>
          <div className="empty-state-action">
            <Button variant="secondary" onClick={() => void loadCart()}>
              Try again
            </Button>
          </div>
        </div>
      </section>
    )
  }

  if (!cart) {
    return null
  }

  // Client-side paging of the line-item table (see CART_PAGE_SIZE). `currentPage` is
  // derived-and-clamped so removing lines on the last page can't strand the view on a
  // now-empty page — it falls back to the last valid page without an extra effect.
  const pageCount = Math.max(1, Math.ceil(cart.lines.length / CART_PAGE_SIZE))
  const currentPage = Math.min(page, pageCount)
  const pageStart = (currentPage - 1) * CART_PAGE_SIZE
  const pagedLines = cart.lines.slice(pageStart, pageStart + CART_PAGE_SIZE)
  // toggleSelectAll needs the currently-visible ids without becoming a
  // useCallback dependency dance — a plain ref updated every render is
  // simpler than threading pagedLines through a memoized callback.
  pagedLinesRef.current = pagedLines.map((line) => line.sourceable.id)

  // UID-271 — the picker's selected tier is derived from the server's
  // persisted `customLeadTimeSelections` (matched by sourceableId, per any
  // line in the "custom" readiness bucket — see `customBucketSourceableIdsOf`'s
  // own doc for why "configurable" is deliberately excluded, code-review
  // M2), NOT a local default — so a reload/refetch always shows whatever
  // was actually chosen last, per Part A #2. Falls back to "us" (Expedited)
  // only when no selection has ever been persisted for this cart, matching
  // the server's own default-to-standard-tier pricing behavior when
  // `customLeadTimeSelections` is absent/empty.
  const customBucketSourceableIds = new Set(customBucketSourceableIdsOf(cart.lines))
  // Code-review Fix 4 — `.find` deliberately takes the FIRST matching
  // selection: the UI exposes exactly one control for the whole custom
  // bucket, so under normal operation every custom-bucket line carries the
  // SAME persisted tier and "first" is as good as "any". A genuinely mixed
  // persisted state (different lines with different tiers) can only arise
  // transiently, from a failed multi-line `handleSelectCustomLeadTimeTier`
  // round trip (H1) — and that path already refetches `GET /cart` on
  // failure, so this render always sees the server's own current truth,
  // not a state this component invented.
  const persistedCustomLeadTimeSelection = cart.customLeadTimeSelections?.find((selection) =>
    customBucketSourceableIds.has(selection.sourceableId),
  )
  // Code-review M1 — the contract types `leadTimeTierId` as an evolvable
  // bare `string`, so a persisted id this FE table doesn't recognize is
  // possible. Guard it explicitly: an unrecognized id becomes `null`
  // ("unresolved"), NEVER a coerced `RegionTierId` — the picker renders a
  // neutral, non-committal state for `null` rather than a confident (and
  // possibly wrong) tier/price. Only "no selection at all yet" falls back
  // to `'us'`, matching the server's own default-to-standard-tier pricing.
  const selectedCustomLeadTimeTierId: RegionTierId | null = persistedCustomLeadTimeSelection
    ? isKnownRegionTierId(persistedCustomLeadTimeSelection.leadTimeTierId)
      ? persistedCustomLeadTimeSelection.leadTimeTierId
      : null
    : 'us'

  const presentationsBySourceableId = new Map(
    cart.linePresentations.map((presentation) => [presentation.sourceableId, presentation]),
  )
  // UID-434/WS-E — the sparse per-line specification sidecar (UID-434 WS-C):
  // only lines that have actually been configuration-edited get an entry, so
  // "no entry" and "no `specificationsBySourceableId.get(id)`" both correctly
  // read as "never configured" rather than defaulting to an empty object.
  const specificationsBySourceableId = new Map(
    (cart.lineSpecifications ?? []).map((entry) => [entry.sourceableId, entry.specification]),
  )
  // UID-411 — the real-thumbnail sidecar (`CartViewWithLineSpecifications.lineThumbnails`),
  // same sparse-join posture as `specificationsBySourceableId` above: only lines with an
  // actual resolved Library thumbnail get an entry, everything else falls back to the
  // existing placeholder glyph.
  const thumbnailsBySourceableId = new Map(
    (cart.lineThumbnails ?? []).map((entry) => [entry.sourceableId, entry.thumbnailUrl]),
  )
  const hasLines = cart.lines.length > 0
  // Only mark each line with its source order when the cart mixes 2+ distinct
  // reorder sources (UID-94) — otherwise the single cart-level notice suffices.
  const markSourceOrder = distinctSourceOrders(cart.linePresentations).length > 1
  // UID-96 (C3) — the order-level SHIP date for ship-together held-back
  // detection. Code-review W1: this used to pass `cart.summary.arrivalEstimateDate`
  // — an ARRIVAL estimate (ship + transit) — which systematically over-reported
  // "could ship earlier": any line ready AFTER the order ships but BEFORE it
  // arrives falsely showed the strike, because a ship date was being compared
  // against an arrival date. The prototype's own test is ship-vs-ship
  // (`_readyDt < _shipDt`). We have no order-level ship field in the contract,
  // but in ship-together mode the order can't leave until its slowest line is
  // ready, so the order ship date IS the LATEST line ship date — derivable
  // from the presentations we already have, ship-side, no new field. A line is
  // then held back only when its own ship date is strictly earlier than that
  // governing date: a true, conservative ship-vs-ship comparison.
  const orderShipDate = cart.linePresentations
    .map((p) => p.shipDate)
    .filter((d): d is string => d != null)
    .reduce<string | null>((latest, d) => (latest === null || d > latest ? d : latest), null)
  // `exactOptionalPropertyTypes` rejects an explicit `undefined` for these
  // optional props, so build one plain object of only the keys actually present
  // rather than inline ternaries in the JSX. The per-row label is a BARE date
  // (`formatBareShipDateLabel`), never `arrivalEstimateLabel` ("Arrives … · One
  // shipment") — the wrong granularity for a per-row "Ship date" cell.
  const lineDateProps = {
    ...(cart.shipPlan?.mode !== undefined ? { mode: cart.shipPlan.mode } : {}),
    ...(orderShipDate !== null
      ? {
          orderShipDate,
          orderShipDateLabel: formatBareShipDateLabel(orderShipDate),
        }
      : {}),
  }

  return (
    <section aria-labelledby="cart-heading" className="cart-screen">
      <div className="cart-header-row">
        <div>
          {/* tabIndex={-1}: programmatically focusable (not in the tab order) so
              handleRemove can move focus here after a line is removed (WCAG F5). */}
          <h1 id="cart-heading" ref={headingRef} tabIndex={-1} className="display-heading">
            Cart
          </h1>
          {/* UID-60: shown even when the cart is empty (0 items · 0 units) —
              only the header actions (add/copy/upload/clear) are gated on
              `hasLines`, below. */}
          <p className="cart-header-subtitle">
            {cart.summary.lineCount} item{cart.summary.lineCount === 1 ? '' : 's'} ·{' '}
            {cart.summary.unitCount} unit{cart.summary.unitCount === 1 ? '' : 's'}
          </p>
        </div>
        {hasLines && (
          <div className="cart-header-actions">
            <Button
              variant="outline-brand"
              onClick={() => setShowAddRow((current) => !current)}
              aria-expanded={showAddRow}
            >
              <PlusOutlined aria-hidden="true" />
              Add parts
            </Button>
            <Button variant="outline" onClick={() => setShowCopyFromExcel(true)}>
              <ExcelCopyIcon />
              Copy from Excel
            </Button>
            <CsvImportControl
              onParsed={(result) => void handleParsedBulkRows(result)}
              className="cart-header-csv-import"
            />
            {/* Design-fidelity fix: clears immediately (no confirm dialog — see
                `handleClearCart`'s doc) and the label is "Clear", not "Clear
                cart" (prototype). */}
            <Button variant="danger-outline" onClick={() => void handleClearCart()}>
              <TrashIcon />
              Clear
            </Button>
          </div>
        )}
      </div>

      {loadError && (
        <div role="alert" className="cart-inline-error">
          <p className="form-error-text">{loadError}</p>
          <button
            type="button"
            className="cart-inline-error-dismiss"
            aria-label="Dismiss"
            onClick={() => setLoadError(undefined)}
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      )}

      {importErrors.length > 0 && (
        <div role="alert" className="cart-import-errors">
          <div className="cart-import-errors-head">
            <p>Some rows couldn&rsquo;t be imported:</p>
            <button
              type="button"
              className="cart-import-errors-dismiss"
              aria-label="Dismiss"
              onClick={() => setImportErrors([])}
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <ul className="cart-import-errors-list">
            {/* S3: duplicate messages are legitimate (e.g. two rows with the
                same unresolved SKU), so a composite index+message key avoids
                React key collisions that would drop/merge list items. A large
                error set (e.g. a whole spreadsheet of unresolved rows) is
                CAPPED to a preview + "+N more" summary and the list itself
                scrolls, so it can't swallow half the screen. */}
            {importErrors.slice(0, CART_IMPORT_ERROR_PREVIEW).map((message, index) => (
              <li key={`${index}:${message}`}>{message}</li>
            ))}
          </ul>
          {importErrors.length > CART_IMPORT_ERROR_PREVIEW && (
            <p className="cart-import-errors-more">
              +{importErrors.length - CART_IMPORT_ERROR_PREVIEW} more{' '}
              {importErrors.length - CART_IMPORT_ERROR_PREVIEW === 1 ? 'row' : 'rows'}{' '}
              couldn&rsquo;t be imported.
            </p>
          )}
        </div>
      )}

      {!hasLines ? (
        <CartEmptyState
          onAdd={handleAddLine}
          onParsedBulkRows={handleParsedBulkRows}
          resolver={skuResolver}
          onOpenCopyFromExcel={() => setShowCopyFromExcel(true)}
        />
      ) : (
        <div className="cart-layout">
          <div className="cart-lines-col">
            <ShipPlanCard
              shipPlan={cart.shipPlan}
              deliveryOptions={cart.deliveryOptions}
              deliveryOptionTotals={cart.deliveryOptionTotals}
              busy={cartMutationPending}
              onSelectMode={(mode) => void handleSelectMode(mode)}
              onSelectDeliveryOption={(id) => void handleSelectDeliveryOption(id)}
              lines={cart.lines}
              presentationsBySourceableId={presentationsBySourceableId}
              customLeadTimeTierId={selectedCustomLeadTimeTierId}
              onSelectCustomLeadTimeTier={(tierId) => void handleSelectCustomLeadTimeTier(tierId)}
              customLeadTimeTierBusy={customLeadTimeTierBusy}
            />

            <ReorderSourceNotice presentations={cart.linePresentations} />

            {/* Selection bar sits AFTER the reorder notice (design order):
                reorder context first, then the bulk-action bar directly above
                the table it acts on. */}
            {selectedIds.size > 0 && (
              <div className="cart-bulk-select-bar">
                <span className="cart-bulk-select-count">{selectedIds.size} selected</span>
                {/* Actions grouped and right-aligned (design): a red OUTLINE
                    "Remove selected" with a trash icon (not a solid fill) and a
                    plain dark "Clear" text link (not a blue underline). */}
                <div className="cart-bulk-select-actions">
                  <Button
                    variant="danger-outline"
                    size="sm"
                    onClick={() => void handleRemoveSelected()}
                  >
                    <TrashIcon />
                    Remove selected
                  </Button>
                  <button
                    type="button"
                    className="cart-bulk-select-clear"
                    onClick={() => rangeSelection.clear()}
                  >
                    Clear
                  </button>
                </div>
              </div>
            )}

            {/* Design-fidelity fix (matching the rendered design file): the
                line table sits in a bordered, rounded card — same treatment as
                ShipPlanCard above it — instead of a bare table with only cell hairlines.
                `overflow: hidden` (cart.css) clips the header/row hairlines to the
                card's rounded corners. The inline "Add parts" row (toggled by the
                header "Add parts" button) renders INSIDE this table, below the
                column header — see CartLineTable. */}
            <Card className="cart-line-table-card">
              <CartLineTable
                lines={pagedLines}
                presentationsBySourceableId={presentationsBySourceableId}
                specificationsBySourceableId={specificationsBySourceableId}
                thumbnailsBySourceableId={thumbnailsBySourceableId}
                markSourceOrder={markSourceOrder}
                selectedIds={selectedIds}
                busyLineIds={busyLineIds}
                {...lineDateProps}
                onToggleSelected={toggleSelected}
                onToggleSelectAll={toggleSelectAll}
                onQuantityChange={(id, next) => void handleQuantityChange(id, next)}
                onRemove={(id) => void handleRemove(id)}
                onConfigurationSaved={handleConfigurationSaved}
                showAddRow={showAddRow}
                onAddLine={handleAddLine}
                onCancelAddRow={() => setShowAddRow(false)}
              />
            </Card>

            {/* Add-on services (design: purple-wrench card after the table).
                UID-423/ADR-0127: requestedServices is PERSISTED — remove
                round-trips through DELETE /cart/services/{id}. UID-424: these
                are populated ONLY by upstream processes (BOM/RFQ/other) via
                the API — there is no add affordance on the cart screen. */}
            <CartServicesSection
              services={requestedServices}
              busyIds={busyServiceIds}
              onRemove={(id) => void handleRemoveService(id)}
            />

            {pageCount > 1 && (
              <nav className="cart-pagination" aria-label="Cart pages">
                <Button
                  variant="secondary"
                  disabled={currentPage <= 1}
                  onClick={() => setPage(currentPage - 1)}
                >
                  Previous
                </Button>
                <span className="cart-pagination-status" aria-live="polite">
                  Page {currentPage} of {pageCount}
                  <span className="sr-only"> ({cart.lines.length} items)</span>
                </span>
                <Button
                  variant="secondary"
                  disabled={currentPage >= pageCount}
                  onClick={() => setPage(currentPage + 1)}
                >
                  Next
                </Button>
              </nav>
            )}
          </div>

          <aside className="cart-summary-col">
            <OrderSummaryRail
              cart={cart}
              savingShipTo={savingShipTo}
              onShipToChange={(id) => void handleShipToChange(id)}
              onCreateAddress={handleCreateShipToAddress}
              onProceedToCheckout={handleProceedToCheckout}
              autoOpenGenerateQuote={autoOpenGenerateQuote}
              onQuoteGenerated={() => void handleQuoteGenerated()}
              disabled={cartMutationPending}
            />
            {/* "Separate POs per group" (design: summary rail, below the order
                summary). Real data now (UID-315, ADR-0106): groups + choice come
                from the server-derived CartView; the toggle persists via
                PUT /cart/split-po and carries the choice to checkout. */}
            <CartSplitPoCard
              groups={poGroups}
              enabled={splitPoEnabled}
              onToggle={(enabled) => void handleToggleSplitPo(enabled)}
              disabled={cartMutationPending}
            />
            {/* Design-fidelity fix (per the user's rendered design screenshot):
                "Multiply all cart quantities" is its OWN row below the
                order-summary card, not nested inside it — reversing the
                earlier UID-131 move that put it inside `OrderSummaryRail`. */}
            <QuantityMultiplier
              appliedMultiplier={appliedMultiplier}
              onApply={(multiplier) => void handleApplyMultiplier(multiplier)}
              onReset={() => void handleResetMultiplier()}
              disabled={cartMutationPending}
            />
          </aside>
        </div>
      )}

      {/* UID-433 gap #4 fix (code-review C1) — ONE modal instance, driven by
          THIS component's own `showCopyFromExcel` state, shared by both the
          populated-cart header button above and `CartEmptyState`'s tile
          below (via its `onOpenCopyFromExcel` prop). An earlier version gave
          `CartEmptyState` its own independent seeded state, which could not
          be told "the user already dealt with this" once the OTHER layout
          took over — reopening the modal on an empty→populated transition
          (right after a successful import) or a populated→empty one (after
          Clear). A single source of truth removes that failure mode by
          construction: there is no second flag left believing the intent is
          still live. Rendered unconditionally (not gated on `hasLines`) —
          exactly one instance exists, so there's nothing to double-mount. */}
      <CopyFromExcelModal
        open={showCopyFromExcel}
        onClose={() => setShowCopyFromExcel(false)}
        onParsed={(result) => void handleParsedBulkRows(result)}
      />
    </section>
  )
}

function CartEmptyState({
  onAdd,
  onParsedBulkRows,
  resolver,
  onOpenCopyFromExcel,
}: {
  readonly onAdd: (sourceable: Sourceable, quantity: Quantity) => Promise<void>
  readonly onParsedBulkRows: (result: {
    rows: readonly CartCsvRow[]
    errors: readonly string[]
  }) => Promise<void>
  readonly resolver: SkuResolveService
  /** UID-433 gap #4 (code-review C1 fix) — opens `CartPage`'s OWN
   * `CopyFromExcelModal` instance rather than this component owning a
   * second, independently-seeded one. Two coordinating pieces of state for
   * "is the modal open" could disagree with each other across an
   * empty↔populated transition (this component unmounts/remounts on every
   * such transition); a single source of truth in the parent can't. */
  readonly onOpenCopyFromExcel: () => void
}) {
  return (
    <div className="cart-empty-state">
      <span className="cart-empty-icon" aria-hidden="true">
        <CartOutlineIcon />
      </span>
      <p className="cart-empty-title">Your cart is empty</p>
      <p className="cart-empty-detail">Know the part number? Add it straight to your cart below.</p>
      <div className="card cart-empty-add-card">
        <p className="cart-empty-add-card-heading">Add by part number</p>
        <AddLineForm onAdd={onAdd} variant="empty-cart" resolver={resolver} />
      </div>
      <div className="cart-empty-divider">
        <span />
        <span>or add in bulk</span>
        <span />
      </div>
      <div className="cart-bulk-tiles">
        <button type="button" className="cart-bulk-tile" onClick={onOpenCopyFromExcel}>
          <span className="cart-bulk-tile-icon" aria-hidden="true">
            <ExcelCopyIcon />
          </span>
          <span className="cart-bulk-tile-body">
            <span className="cart-bulk-tile-title">Copy from Excel</span>
            <span className="cart-bulk-tile-detail">
              Paste a Part # / Qty table — we add every recognized part.
            </span>
          </span>
          <span className="cart-bulk-tile-chevron" aria-hidden="true">
            <ChevronRightIcon />
          </span>
        </button>
        <CsvImportControl onParsed={(result) => void onParsedBulkRows(result)} tile />
      </div>
    </div>
  )
}

/** Lucide chevron-right for the empty-cart bulk tiles (design uses a stroke
 * caret, not the Ant filled `RightOutlined`). */
function ChevronRightIcon() {
  return (
    <svg
      aria-hidden="true"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m9 18 6-6-6-6" />
    </svg>
  )
}

function CartOutlineIcon() {
  return (
    <svg
      aria-hidden="true"
      width="26"
      height="26"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="8" cy="21" r="1" />
      <circle cx="19" cy="21" r="1" />
      <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12" />
    </svg>
  )
}

/** "Clear" header icon — the Lucide "trash-2" glyph (lid + two vertical
 * lines) the design uses, replacing the Ant filled `DeleteOutlined` (a plain
 * bin with no inner lines). Inherits the button's red `currentColor`. */
function TrashIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
      <line x1="10" x2="10" y1="11" y2="17" />
      <line x1="14" x2="14" y1="11" y2="17" />
    </svg>
  )
}
